import * as Sentry from '@sentry/react';
import { Download } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import {
    type ArtifactDownload,
    type ArtifactDownloadSource,
    buildJsonPayloadDownload,
    getArtifactDownload,
} from '@apps/web/routes/_app/admin/agent/artifacts/download';
import { type AdminAgentArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/frame';

const BUTTON_CLASSNAME =
    'text-muted-foreground hover:text-foreground focus-visible:ring-ring/40 relative -my-1 -mr-1 inline-flex size-8 items-center justify-center rounded-md transition-[color,background-color,transform] duration-150 ease-out outline-none hover:bg-[var(--aa-surface,#f7f7f5)] focus-visible:ring-2 active:scale-[0.96]';
const DISABLED_BUTTON_CLASSNAME =
    'text-muted-foreground/40 relative -my-1 -mr-1 inline-flex size-8 cursor-not-allowed items-center justify-center rounded-md';

export function ArtifactDownloadButton({
    artifact,
}: {
    readonly artifact: AdminAgentArtifact;
}) {
    const apiUrl = resolveApiUrlOrEmpty();
    const primary = useMemo(
        () => getArtifactDownload(artifact, apiUrl),
        [artifact, apiUrl],
    );
    const { effective, href, status } = useResolvedDownload(artifact, primary);

    // No download can be prepared at all (payload serializes to nothing and
    // no href source). Prefer visible failure over silent unmount even so:
    // render a disabled indicator whenever a primary source existed but
    // could not be built.
    if (!effective) {
        return null;
    }
    if (status === 'failed' || !href) {
        return (
            <span
                aria-disabled={true}
                aria-label="Download unavailable"
                className={DISABLED_BUTTON_CLASSNAME}
                data-testid="admin-artifact-download"
                data-download-status="failed"
                role="button"
                title="Download unavailable — the artifact payload could not be prepared"
            >
                <Download className="size-3.5" aria-hidden={true} />
            </span>
        );
    }
    return (
        <a
            aria-label={`Download ${effective.filename}`}
            className={BUTTON_CLASSNAME}
            data-testid="admin-artifact-download"
            data-download-status={status === 'fallback' ? 'fallback' : 'ok'}
            download={effective.filename}
            href={href}
            title={`Download ${effective.filename}`}
        >
            <Download className="size-3.5" aria-hidden={true} />
        </a>
    );
}

type ResolvedDownloadState = 'ok' | 'fallback' | 'failed';

function useResolvedDownload(
    artifact: AdminAgentArtifact,
    primary: ArtifactDownload | null,
): {
    readonly effective: ArtifactDownload | null;
    readonly href: string | null;
    readonly status: ResolvedDownloadState;
} {
    // Reason a swap happens is source-kind failure, NOT filename change. A
    // filename-equality gate silently unmounts the button when the primary
    // artifact happens to share a title-derived filename with its JSON
    // fallback (e.g. an admin.file.json@1 artifact titled "Corrupt report"
    // whose primary and fallback both slug to `corrupt-report.json`), which
    // is exactly the failure this fallback exists to prevent.
    const [status, setStatus] = useState<ResolvedDownloadState>('ok');
    const fallback = useMemo(
        () => buildJsonPayloadDownload(artifact),
        [artifact],
    );

    // Reset back to primary whenever the artifact or its resolved download
    // changes so a prior fallback swap does not stick across artifacts.
    useEffect(() => {
        setStatus('ok');
    }, [primary]);

    const effective = status === 'ok' ? primary : (fallback ?? primary);
    const source = effective?.source ?? null;
    const [blobHref, setBlobHref] = useState<string | null>(null);

    useEffect(() => {
        if (source === null || source.kind === 'href') {
            setBlobHref(null);
            return;
        }
        let url: string;
        try {
            const blob = buildBlobFromSource(source);
            url = URL.createObjectURL(blob);
        } catch (error) {
            Sentry.captureException(
                error instanceof Error
                    ? error
                    : new Error('admin artifact download blob failed'),
                {
                    tags: {
                        area: 'admin-artifact-download',
                        artifact_type: artifact.artifact_type,
                        download_status: status,
                    },
                },
            );
            setBlobHref(null);
            // Escalate: primary blob failed → try the JSON dump fallback.
            // Fallback JSON also failed → surface a disabled error
            // indicator so the user and reviewers can see the corruption
            // instead of the button silently disappearing.
            setStatus((current) => (current === 'ok' ? 'fallback' : 'failed'));
            return;
        }
        setBlobHref(url);
        return () => URL.revokeObjectURL(url);
    }, [source, artifact, status]);

    if (source === null) {
        return { effective, href: null, status };
    }
    if (source.kind === 'href') {
        return { effective, href: source.href, status };
    }
    return { effective, href: blobHref, status };
}

// Runtime config is initialized in the running app but not in unit tests that
// render an artifact directly. Falling back to a same-origin relative URL keeps
// image-href downloads sane in that context instead of throwing at render.
function resolveApiUrlOrEmpty(): string {
    try {
        return getRuntimeConfig().apiUrl;
    } catch {
        return '';
    }
}

function buildBlobFromSource(source: ArtifactDownloadSource): Blob {
    if (source.kind === 'text') {
        return new Blob([source.content], { type: source.contentType });
    }
    if (source.kind === 'base64') {
        const binary = atob(source.data);
        const bytes = Uint8Array.from(binary, (character) =>
            character.charCodeAt(0),
        );
        return new Blob([bytes], { type: source.contentType });
    }
    throw new Error(`Unsupported download source: ${String(source)}`);
}
